import { type Table } from '@tanstack/react-table'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { X } from 'lucide-react'

export interface Filters {
  search?: string
  status?: string
  workflow?: string
  phenotype?: string
}

interface DataTableToolbarProps<TData> {
  table: Table<TData>
  filters: Filters
  onFiltersChange: (filters: Filters) => void
}

const statusOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'queued', label: 'Queued' },
  { value: 'processing', label: 'Processing' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
]

const workflowOptions = [
  { value: 'all', label: 'All Workflows' },
  { value: 'router', label: 'Router' },
  { value: 'standard', label: 'Standard' },
  { value: 'advanced', label: 'Advanced' },
]

export function DataTableToolbar<TData>({
  table: _table,
  filters,
  onFiltersChange,
}: DataTableToolbarProps<TData>) {
  // Note: _table is available for future column-based filtering
  const isFiltered =
    filters.search ||
    (filters.status && filters.status !== 'all') ||
    (filters.workflow && filters.workflow !== 'all') ||
    filters.phenotype

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-1 items-center gap-2">
        <Input
          placeholder="Search by filename..."
          value={filters.search || ''}
          onChange={(e) =>
            onFiltersChange({ ...filters, search: e.target.value })
          }
          className="h-8 w-[150px] lg:w-[250px]"
        />
        <Select
          value={filters.status || 'all'}
          onValueChange={(value) =>
            onFiltersChange({
              ...filters,
              status: value === 'all' ? undefined : value,
            })
          }
        >
          <SelectTrigger className="h-8 w-[130px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {statusOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.workflow || 'all'}
          onValueChange={(value) =>
            onFiltersChange({
              ...filters,
              workflow: value === 'all' ? undefined : value,
            })
          }
        >
          <SelectTrigger className="h-8 w-[130px]">
            <SelectValue placeholder="Workflow" />
          </SelectTrigger>
          <SelectContent>
            {workflowOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {filters.phenotype && (
          <Badge variant="secondary" className="h-8 flex items-center gap-1">
            Phenotype: {filters.phenotype.length > 8
              ? `${filters.phenotype.slice(0, 8)}...`
              : filters.phenotype}
            <X
              className="h-3 w-3 cursor-pointer"
              onClick={() => onFiltersChange({ ...filters, phenotype: undefined })}
            />
          </Badge>
        )}
        {isFiltered && (
          <Button
            variant="ghost"
            onClick={() =>
              onFiltersChange({
                search: '',
                status: undefined,
                workflow: undefined,
                phenotype: undefined,
              })
            }
            className="h-8 px-2 lg:px-3"
          >
            Reset
            <X className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  )
}
